<template>
  <q-card
    flat
    bordered
    class="col-12"
  >
    <q-card-section
      class="q-py-sm bg-grey-2"
    >
      <div class="row items-center">
        <div
          :class="{'light-dimmed':isStatusDeleted}"
        >
          <status-chip
            :target-status="status"
            size="md"
            :editable="!extendWorkflow && userCanEditWorkflow"
            :statuses="workflowStatuses"
            @edit="onEditStatus"
          />
          <template v-if="!onlyStatus">
            <q-icon
              name="arrow_forward"
              size="xs"
              color="grey-6"
            />
            <status-chip
              :target-status="nextStatus"
              :statuses="workflowStatuses"
              size="xs"
            />
          </template>
        </div>
        <q-space />
        <q-btn
          v-if="!extendWorkflow && isStatusDeleted"
          icon="restore_from_trash"
          color="dark"
          round
          dense
          flat
          @click="onRestoreStatus"
        />
      </div>
    </q-card-section>
    <template
      v-if="!isStatusDeleted && !onlyStatus"
    >
      <q-separator />

      <q-card-section
        v-if="isMaintenanceWorkflow"
      >
        <q-table
          v-model:pagination="maintenancePagination"
          :columns="maintenanceColumns"
          :rows="maintenanceSchedule"
          :no-data-label="$t('programs.workflows.workflowDetails.noMaintenanceSchedule')"
          flat
          hide-pagination
          class="q-mb-md"
        >
          <template #body-cell-sessionCount="props">
            <q-td
              :props="props"
            >
              <div class="row justify-end items-baseline">
                <div class="col-auto q-px-sm">
                  {{ $t('programs.workflows.workflowDetails.maintenanceIntervalPrefix') }}
                </div>
                <q-input
                  :ref="'maintenanceInterval_'+props.row.interval"
                  :model-value="props.row.sessions"
                  type="number"
                  :readonly="!userCanEditWorkflow"
                  dense
                  hide-bottom-space
                  flat
                  lazy-rules
                  :rules="rules.interval"
                  :suffix="getMaintenanceSuffix(props.row.interval, props.row.sessions)"
                  :input-class="'text-right ' +
                    isValueAdjustedDeep('maintenanceSchedule', {interval: props.row.interval})"
                  @update:model-value="onInputMaintenanceSessions(props.row.interval, $event)"
                />
              </div>
            </q-td>
          </template>
          <template
            v-if="userCanEditWorkflow"
            #body-cell-actions="props"
          >
            <q-td
              :props="props"
              auto-width
            >
              <q-btn
                v-if="hasMultipleMaintenanceIntervals"
                icon="delete"
                dense
                flat
                round
                size="sm"
                color="dark"
                @click="onRemoveMaintenanceInterval(props.row.interval)"
              />
            </q-td>
          </template>
          <template #bottom-row>
            <q-tr>
              <q-td colspan="100%">
                <q-btn-dropdown
                  v-if="canAddMaintenanceInterval"
                  v-model="maintenanceIntervalMenu"
                  :label="$t('programs.workflows.workflowDetails.btnAddMaintenanceIntervalLabel')"
                  flat
                  color="primary"
                >
                  <q-list>
                    <q-item
                      v-for="intervalInfo in unusedMaintenanceIntervals"
                      :key="intervalInfo.value"
                      v-close-popup
                      clickable
                      @click="onAddMaintenanceInterval(intervalInfo.value, $event)"
                    >
                      <q-item-section>
                        <q-item-label>
                          {{ intervalInfo.label }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-btn-dropdown>
              </q-td>
            </q-tr>
          </template>
        </q-table>
        <div class="col-12">
          <banner
            :message="$t('programs.workflows.workflowDetails.maintenanceHint')"
          />
        </div>
      </q-card-section>
      <q-card-section>
        <div class="row col-12 items-baseline">
          <data-input
            v-model="workflowVal"
            :label="metricLabel"
            :metric="workflowType?.metric"
            :readonly="!userCanEditWorkflow"
            :rules="rules[workflowFieldType]"
            lazy-rules
            hide-bottom-space
            :input-class="'text-right ' + isValueAdjusted(workflowProp)"
            class="col"
          />
          <template
            v-if="!isMaintenanceWorkflow"
          >
            <div class="col-auto q-px-sm">
              {{ $t('programs.workflows.workflowDetails.inputSessionLabel') }}
            </div>
            <q-input
              v-model.number="sessions"
              type="number"
              label=""
              :suffix="$t('programs.workflows.workflowDetails.inputSessionSuffix')"
              :readonly="!userCanEditWorkflow"
              :rules="rules.sessions"
              lazy-rules
              :input-class="'text-right ' + isValueAdjusted('sessions')"
              class="col"
            />
          </template>
        </div>
        <q-input
          v-if="hasMinNumberOfTrials"
          v-model.number="minTrials"
          type="number"
          :readonly="!userCanEditWorkflow"
          :label="$t('programs.workflows.workflowDetails.inputTrialsLabel')"
          :suffix="$t('programs.workflows.workflowDetails.suffixTrials')"
          :input-class="'text-right ' + isValueAdjusted('minTrials')"
          class="col-12"
          min="0"
          lazy-rules
          :rules="rules.minTrials"
        />
        <q-expansion-item
          :model-value="showAdvancedSettings"
          :label="$t('programs.workflows.workflowDetails.moreSettingsLabel')"
          dense
          class="col-12"
          @update:model-value="onToggleAdvancedSettings"
        >
          <div class="row q-col-gutter-md">
            <q-select
              v-if="isProbeWorkflow"
              ref="validateFailedStatus"
              v-model="failedStatus"
              :options="failedTargetStatuses"
              :readonly="!userCanEditWorkflow"
              hide-bottom-space
              :label="$t('programs.workflows.workflowDetails.selectProbeToStatusLabel')"
              map-options
              emit-value
              :rules="[validateProbeWorkflow]"
              class="col-12"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                >
                  <q-item-section
                    avatar
                  >
                    <status-chip
                      :target-status="scope.opt.value"
                      :statuses="workflowStatuses"
                    />
                  </q-item-section>
                </q-item>
              </template>
              <template #selected-item="scope">
                <q-icon
                  v-if="isValueAdjusted('failedStatus')"
                  name="warning"
                  color="red"
                >
                  <q-tooltip>{{ $t('programs.workflows.workflowDetails.textChanged') }}</q-tooltip>
                </q-icon>
                <status-chip
                  :target-status="scope.opt.value"
                  :statuses="workflowStatuses"
                />
              </template>
            </q-select>
            <q-select
              v-if="isProbeWorkflow"
              ref="validateMasteredStatus"
              v-model="masteredStatus"
              :options="masteredTargetStatuses"
              :readonly="!userCanEditWorkflow"
              :label="$t('programs.workflows.workflowDetails.selectMasteredStatusLabel')"
              map-options
              emit-value
              hide-bottom-space
              :rules="[validateProbeWorkflow]"
              class="col-12"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                >
                  <q-item-section avatar>
                    <status-chip
                      :target-status="scope.opt.value"
                      :statuses="workflowStatuses"
                    />
                  </q-item-section>
                </q-item>
              </template>
              <template #selected-item="scope">
                <q-icon
                  v-if="isValueAdjusted('masteredStatus')"
                  name="warning"
                  color="red"
                >
                  <q-tooltip>{{ $t('programs.workflows.workflowDetails.textChanged') }}</q-tooltip>
                </q-icon>
                <status-chip
                  :target-status="scope.opt.value"
                  :statuses="workflowStatuses"
                />
              </template>
            </q-select>
            <q-input
              v-if="hasMinNumberOfTrials && !isMaintenanceWorkflow"
              ref="validateLimitTrials"
              v-model.number="limitTrials"
              type="number"
              :readonly="!userCanEditWorkflow"
              :label="$t('programs.workflows.workflowDetails.inputLimitTrialsLabel')"
              :suffix="$t('programs.workflows.workflowDetails.suffixTrials')"
              :hint="$t('common.hintOptional')"
              :input-class="'text-right ' + isValueAdjusted('limitTrials')"
              class="col-12"
              :rules="rules.limitTrials"
              lazy-rules
              min="1"
            />
            <div class="col-12 row">
              <q-checkbox
                v-model="addPhaseLine"
                :readonly="!userCanEditWorkflow"
                :label="$t('programs.workflows.workflowDetails.checkboxAddPhaseLineLabel', { statusLabel })"
                class="col-12"
              />
              <q-input
                v-if="addPhaseLine"
                v-model="addPhaseLineLabel"
                :readonly="!userCanEditWorkflow"
                dense
                :hint="$t('programs.workflows.workflowDetails.inputPhaseLineLabelLabel')"
                :placeholder="statusLabel"
                :input-class="isValueAdjusted('addPhaseLineLabel')"
                class="col-12"
              />
            </div>
          </div>
        </q-expansion-item>
      </q-card-section>
      <template
        v-if="hasMaintenanceSchedule"
      >
        <q-separator />
        <q-card-section>
          <div class="col-12 row q-col-gutter-sm">
            <q-checkbox
              v-model="revertStrategyCheckbox"
              :disable="!userCanEditWorkflow"
            >
              <status-chip
                target-status="mastered"
                :statuses="workflowStatuses"
              />
              <q-icon
                name="arrow_forward"
                size="xs"
                color="grey-6"
              />
              <status-chip
                :target-status="revertStatus"
                :statuses="workflowStatuses"
              />
            </q-checkbox>
          </div>
          <template v-if="revertStrategy === 'mass_trial'">
            <div class="row col-12 items-baseline">
              <q-input
                v-model.number="sessions"
                type="number"
                :label="$t('programs.workflows.workflowDetails.inputRevertSessions')"
                :suffix="$t('programs.workflows.workflowDetails.inputSessionSuffix')"
                :readonly="!userCanEditWorkflow"
                min="0"
                hide-hint
                lazy-rules
                :rules="rules.revertSessions"
                :input-class="'text-right ' + isValueAdjusted('sessions')"
                class="col"
              />
            </div>
          </template>
        </q-card-section>
      </template>
    </template>
  </q-card>
</template>
<script>
import { measurementMetrics, workflowTypes, WORKFLOW_INTERVALS } from 'boot/globals';
import StatusChip from 'components/Programs/StatusChip';
import Banner from 'components/UI/Banner';
import DataInput from 'components/UI/DataInput';
import { chain, get, isEmpty, map, merge, size, toNumber } from 'lodash';
import Toolbox from 'src/mixins/Toolbox';

export default {
  name: 'EditWorkflowDetails',

  components: { DataInput, StatusChip, Banner },

  mixins: [Toolbox],

  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    status: {
      type: String,
      required: true,
    },
    workflowStatuses: {
      type: Object,
      required: true,
    },
    originalWorkflow: {
      type: Object,
      required: true,
    },
    userCanEditWorkflow: {
      type: Boolean,
      default: false,
    },
    extendWorkflow: {
      type: Boolean,
      default: false,
    },
    hasMinNumberOfTrials: {
      type: Boolean,
      default: false,
    },
    onlyStatus: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['edit-status', 'restore-status', 'update:modelValue'],

  data() {
    return {
      showAdvancedSettings: false,

      maintenanceIntervalMenu: false,
      maintenancePagination: {
        sortBy: 'frequency',
        descending: false,
        page: 1,
        rowsPerPage: 1000,
      },
      maintenanceColumns: [
        {
          name: 'interval',
          field: 'interval',
          align: 'left',
          label: this.$t('programs.workflows.workflowDetails.maintenanceScheduleColumns.intervalLabel'),
          format: (interval) => this.$t(`programs.workflows.workflowDetails.maintenanceIntervals.I${interval}`),
        },
        {
          name: 'sessionCount',
          field: 'sessions',
          label: '',
        },
        {
          name: 'actions',
          label: '',
        },
      ],

      ruleTypes: {
        isNumber: (val) => Number.isInteger(val) || this.$t('common.errors.integerValue'),
        isGreaterThan0Pct: (val) => val > 0 || this.$t('programs.workflows.workflowDetails.inputValidatorMore0Pct'),
        isGreaterOrEqual0Pct: (val) => val >= 0 || this.$t('programs.workflows.workflowDetails.inputValidatorMore0Pct'),
        isLessThan100Pct: (val) => val < 100
          || this.$t('programs.workflows.workflowDetails.inputValidatorLess100'),
        isLessOrEqual100Pct: (val) => val <= 100
          || this.$t('programs.workflows.workflowDetails.inputValidatorLessEq100'),
        isGreaterThan0: (val) => val > 0 || this.$t('programs.workflows.workflowDetails.inputValidatorMore0'),
        isGreaterOrEqual0: (val) => (val >= 0)
          || this.$t('programs.workflows.workflowDetails.inputValidatorMoreEq0'),
        isDurationGreaterThan0: (hms) => this.stringToDuration(hms) > 0
          || this.$t('programs.workflows.workflowDetails.inputValidatorMore0'),
        isDurationGreaterThanEq0: (hms) => this.stringToDuration(hms) >= 0
          || this.$t('programs.workflows.workflowDetails.inputValidatorMoreEq0'),
      },
    };
  },

  computed: {

    rules() {
      return {
        accuracyIncrease: [
          this.ruleTypes.isNumber,
          this.ruleTypes.isGreaterThan0Pct,
          this.ruleTypes.isLessOrEqual100Pct,
        ],
        accuracyReduction: [
          this.ruleTypes.isNumber,
          this.ruleTypes.isGreaterOrEqual0Pct,
          this.ruleTypes.isLessThan100Pct,
        ],
        frequencyIncrease: [
          this.ruleTypes.isNumber,
          this.ruleTypes.isGreaterThan0,
        ],
        frequencyReduction: [
          this.ruleTypes.isNumber,
          this.ruleTypes.isGreaterOrEqual0,
        ],
        ratePerMinuteIncrease: [
          this.ruleTypes.isNumber,
          this.ruleTypes.isGreaterThan0,
        ],
        ratePerMinuteReduction: [
          this.ruleTypes.isNumber,
          this.ruleTypes.isGreaterOrEqual0,
        ],
        ratePerHourIncrease: [
          this.ruleTypes.isNumber,
          this.ruleTypes.isGreaterThan0,
        ],
        ratePerHourReduction: [
          this.ruleTypes.isNumber,
          this.ruleTypes.isGreaterOrEqual0,
        ],
        totalDurationIncrease: [
          this.ruleTypes.isDurationGreaterThan0,
        ],
        totalDurationReduction: [
          this.ruleTypes.isDurationGreaterThanEq0,
        ],
        minDurationIncrease: [
          this.ruleTypes.isDurationGreaterThan0,
        ],
        minDurationReduction: [
          this.ruleTypes.isDurationGreaterThanEq0,
        ],
        maxDurationIncrease: [
          this.ruleTypes.isDurationGreaterThan0,
        ],
        maxDurationReduction: [
          this.ruleTypes.isDurationGreaterThanEq0,
        ],
        avgDurationIncrease: [
          this.ruleTypes.isDurationGreaterThan0,
        ],
        avgDurationReduction: [
          this.ruleTypes.isDurationGreaterThanEq0,
        ],
        sessions: [
          this.ruleTypes.isNumber,
          (val) => val > 0 || this.$t('programs.workflows.workflowDetails.inputSessionValidator'),
        ],
        minTrials: [
          this.ruleTypes.isNumber,
          (val) => val > 0 || this.$t('programs.workflows.workflowDetails.inputTrialsValidator'),
        ],
        limitTrials: [
          (val) => [null, '', undefined].includes(val) || (val > 0)
              || this.$t('programs.workflows.workflowDetails.inputValidatorEmptyOrAtLeast1Trial'),
          (val) => ([null, '', undefined].includes(val) || Number.isInteger(val))
              || this.$t('common.errors.integerValue'),
        ],
        interval: [
          this.ruleTypes.isNumber,
          (val) => val > 0 || this.$t('programs.workflows.workflowDetails.inputValidatorMore0'),
        ],
        revertSessions: [
          this.ruleTypes.isNumber,
          (val) => val > 0 || this.$t('programs.workflows.workflowDetails.inputValidatorMore0'),
        ],
      };
    }, // rules()

    isStatusDeleted() {
      return get(this.modelValue, ['statuses', this.status, 'isDeleted']);
    }, // isStatusDeleted()

    isProbeWorkflow() {
      return this.statusType === 'probe';
    }, // isProbeWorkflow()

    isMaintenanceWorkflow() {
      return this.statusType === 'mastered';
    }, // isMaintenanceWorkflow()

    hasMultipleMaintenanceIntervals() {
      return size(this.maintenanceSchedule) > 1;
    }, // hasMultipleMaintenanceIntervals()

    workflowType() {
      return workflowTypes[this.workflowFieldType] || null;
    }, // workflowType()

    measurementMetric() {
      return measurementMetrics.find((metric) => metric.id === this.workflowType?.metric);
    }, // measurementMetric()

    workflowProp() {
      return this.workflowType?.workflowProp || 'accuracy';
    }, // workflowProp()

    metricLabel() {
      const label = this.measurementMetric?.label || '';
      if (this.workflowType?.direction === 'reduction') {
        return this.$t(
          'programs.workflows.workflowDetails.inputReduceMetricLabel',
          { metric: label },
        );
      }
      return this.$t(
        'programs.workflows.workflowDetails.inputIncreaseMetricLabel',
        { metric: label },
      );
    }, // metricLabel()

    workflowFieldType() {
      return this.modelValue?.type || 'accuracyIncrease';
    }, // workflowFieldType()

    statusLabel() {
      return get(this.workflowStatuses, [this.status, 'label'], null);
    }, // statusLabel()

    statusType() {
      return get(this.workflowStatuses, [this.status, 'type'], null);
    }, // statusType()

    failedTargetStatuses() {
      return chain(this.workflowStatuses)
        .pick(
          'waiting',
          chain(this.workflowStatuses)
            .pickBy({ type: 'acquisition' })
            .keys()
            .value(),
        )
        .values()
        .sortBy('order')
        .value();
    }, // failedTargetStatuses()

    masteredTargetStatuses() {
      return chain(this.workflowStatuses)
        .pick(
          chain(this.workflowStatuses)
            .pickBy({ type: 'acquisition' })
            .keys()
            .value(),
          'mastered',
          'closed',
        )
        .values()
        .sortBy('order')
        .value();
    }, // masteredTargetStatuses()

    nextStatus() {
      if (this.masteredStatus) {
        return this.masteredStatus;
      }

      const currentOrder = chain(this.workflowStatuses)
        .get([this.status, 'order'], 99999999)
        .value();

      return chain(this.workflowStatuses)
        // .filter({ type: this.statusType })
        .reject({ isDeleted: true })
        .sortBy('order')
        .reject((statusObj) => statusObj.order <= currentOrder)
        .head()
        .get('value')
        .value();
    }, // nextStatus()

    revertStatus() {
      if (this.masteredStatus) {
        return this.masteredStatus;
      }

      return chain(this.workflowStatuses)
        .filter({ type: 'acquisition' })
        .reject({ isDeleted: true })
        .sortBy('order')
        .head()
        .get('value', 'probe')
        .value();
    }, // revertStatus()

    maintenanceIntervals() {
      return map(
        [
          WORKFLOW_INTERVALS.DAILY,
          WORKFLOW_INTERVALS.WEEKLY,
          WORKFLOW_INTERVALS.BIWEEKLY,
          WORKFLOW_INTERVALS.MONTHLY,
          WORKFLOW_INTERVALS.QUARTERLY,
          WORKFLOW_INTERVALS.SEMI_ANNUALLY,
          WORKFLOW_INTERVALS.ANNUALLY,
        ],
        (value) => ({
          value,
          label: this.$t(`programs.workflows.workflowDetails.maintenanceIntervals.I${value}`),
        }),
      );
    }, // maintenanceIntervals()

    hasMaintenanceSchedule() {
      return !isEmpty(this.maintenanceSchedule);
    }, // hasMaintenanceSchedule()

    canAddMaintenanceInterval() {
      return size(this.unusedMaintenanceIntervals) > 0;
    }, // canAddMaintenanceInterval()

    unusedMaintenanceIntervals() {
      return chain(this.maintenanceIntervals)
        .reject((intervalItem) => !chain(this.maintenanceSchedule)
          .find({ interval: intervalItem.value })
          .isEmpty()
          .value())
        .value();
    }, // unusedMaintenanceIntervals()

    maintenanceSchedule() {
      return chain(this.modelValue)
        .get(['statuses', this.status, 'maintenanceSchedule'])
        .defaultTo([])
        .value();
    }, // maintenanceSchedule()

    workflowVal: {
      get() {
        return get(this.modelValue, ['statuses', this.status, this.workflowProp], null);
      },
      set(value) {
        this.inputValue(this.workflowProp, value);
      },

    }, // val()

    accuracy: {
      get() {
        return get(this.modelValue, ['statuses', this.status, 'accuracy'], null);
      },
      set(value) {
        this.inputValue('accuracy', value);
      },
    }, // accuracy()

    minFrequency: {
      get() {
        return get(this.modelValue, ['statuses', this.status, 'minFrequency'], null);
      },
      set(value) {
        this.inputValue('minFrequency', value);
      },
    }, // minFrequency()

    maxFrequency: {
      get() {
        return get(this.modelValue, ['statuses', this.status, 'maxFrequency'], null);
      },
      set(value) {
        this.inputValue('maxFrequency', value);
      },
    }, // maxFrequency()

    sessions: {
      get() {
        return get(this.modelValue, ['statuses', this.status, 'sessions']) || 1;
      },
      set(value) {
        this.inputValue('sessions', value);
      },
    }, // sessions()

    minTrials: {
      get() {
        return get(this.modelValue, ['statuses', this.status, 'minTrials']);
      },
      set(value) {
        this.inputValue('minTrials', value);
      },
    }, // minTrials()

    limitTrials: {
      get() {
        return get(this.modelValue, ['statuses', this.status, 'limitTrials'], null);
      },
      set(value) {
        this.inputValue('limitTrials', value);
      },
    }, // limitTrials()

    failedStatus: {
      get() {
        return get(this.modelValue, ['statuses', this.status, 'failedStatus']);
      },
      set(value) {
        this.inputValue('failedStatus', value);
      },
    }, // failedStatus()

    addPhaseLine: {
      get() {
        return get(this.modelValue, ['statuses', this.status, 'addPhaseLine']) || false;
      },
      set(value) {
        this.inputValue('addPhaseLine', value);
      },
    }, // addPhaseLine()

    addPhaseLineLabel: {
      get() {
        return get(this.modelValue, ['statuses', this.status, 'addPhaseLineLabel']);
      },
      set(value) {
        this.inputValue('addPhaseLineLabel', value);
      },
    }, // addPhaseLineLabel()

    masteredStatus: {
      get() {
        return get(this.modelValue, ['statuses', this.status, 'masteredStatus']);
      },
      set(masteredStatus) {
        this.inputValue('masteredStatus', masteredStatus);
      },
    }, // masteredStatus()

    revertStrategy: {
      get() {
        return get(this.modelValue, ['statuses', this.status, 'revertStrategy']);
      },
      set(revertStrategy) {
        this.inputValue('revertStrategy', revertStrategy);
      },
    }, // revertStrategy()

    revertStrategyCheckbox: {
      get() {
        return get(this.modelValue, ['statuses', this.status, 'revertStrategy']) === 'mass_trial';
      },
      set(checked) {
        this.inputValue('revertStrategy', (checked ? 'mass_trial' : null));
      },
    }, // revertStrategyCheckbox()

  },

  methods: {

    onToggleAdvancedSettings(val) {
      if (!val) {
        // Make sure we are allowed to contract the advanced settings (we can't if there are errors)
        const hasErrors = chain(['validateFailedStatus', 'validateMasteredStatus', 'validateLimitTrials'])
          .map((ref) => (this.$refs[ref] && !this.$refs[ref].validate()))
          .some()
          .value();

        if (hasErrors) {
          return;
        }
      }

      this.showAdvancedSettings = val;
    },

    onEditStatus() {
      this.$emit('edit-status', this.status);
    }, // onEditStatus()

    onRestoreStatus() {
      this.$emit('restore-status', this.status);
    }, // onRestoreStatus()

    onAddMaintenanceInterval(interval) {
      const maintenanceSchedule = chain(this.modelValue)
        .get(['statuses', this.status, 'maintenanceSchedule'], [])
        .concat(
          {
            interval,
            sessions: null,
          },
        )
        .sortBy('interval')
        .value();

      this.inputValueDeep('maintenanceSchedule', maintenanceSchedule);

      this.maintenanceIntervalMenu = false;
      this.$nextTick(() => {
        if (this.$refs[`maintenanceInterval_${interval}`]) {
          this.$refs[`maintenanceInterval_${interval}`].focus();
        }
      });
    }, // onAddMaintenanceInterval()

    onInputMaintenanceSessions(interval, sessions) {
      const maintenanceSchedule = chain(this.modelValue)
        .get(['statuses', this.status, 'maintenanceSchedule'], [])
        .map((intervalInfo) => ({
          interval: intervalInfo.interval,
          sessions: intervalInfo.interval === interval ? toNumber(sessions) : intervalInfo.sessions,
        }))
        .sortBy('interval')
        .value();

      this.inputValueDeep('maintenanceSchedule', maintenanceSchedule);
    }, // onInputMaintenanceSessions()

    onRemoveMaintenanceInterval(interval) {
      const intervalLabel = chain(this.maintenanceIntervals)
        .find({ value: interval })
        .get('label')
        .value();
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t(
            'programs.workflows.workflowDetails.confirmDeleteMaintenanceInterval',
            { interval: intervalLabel },
          ),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          const maintenanceSchedule = chain(this.modelValue)
            .get(['statuses', this.status, 'maintenanceSchedule'], [])
            .reject({ interval })
            .sortBy('interval')
            .value();

          this.inputValueDeep('maintenanceSchedule', maintenanceSchedule);
        });
    }, // onRemoveMaintenanceInterval()

    inputValue(parameter, value) {
      this.$emit(
        'update:modelValue',
        merge({}, this.modelValue, { statuses: { [this.status]: { [parameter]: value } } }),
      );
    }, // inputValue()

    inputValueDeep(parameter, value) {
      this.$emit(
        'update:modelValue',
        chain(this.modelValue)
          .cloneDeep()
          .set(['statuses', this.status, parameter], value)
          .value(),
      );
    }, // inputValue()

    isValueAdjusted(param) {
      if (!this.extendWorkflow) {
        return '';
      }
      if (!chain(this.modelValue)
        .get(['statuses', this.status, param])
        .isEqual(get(this.originalWorkflow, ['statuses', this.status, param]))
        .value()) {
        return 'text-bold text-red';
      }
      return '';
    }, // isValueAdjusted()

    isValueAdjustedBg(param) {
      if (!this.extendWorkflow) {
        return '';
      }
      if (!chain(this.modelValue)
        .get(['statuses', this.status, param])
        .isEqual(get(this.originalWorkflow, ['statuses', this.status, param]))
        .value()) {
        return 'text-bold bg-red';
      }
      return '';
    }, // isValueAdjusted()

    isValueAdjustedDeep(param, param2) {
      if (!this.extendWorkflow) {
        return '';
      }
      if (!chain(this.modelValue)
        .get(['statuses', this.status, param])
        .find(param2)
        .isEqual(chain(this.originalWorkflow).get(['statuses', this.status, param]).find(param2).value())
        .value()) {
        return 'text-bold text-red';
      }
      return '';
    }, // isValueAdjustedDeep()

    formatWorkflowDuration(workflowParameter) {
      return this.formatDuration(get(this.modelValue, ['statuses', this.status, workflowParameter]));
    }, // formatWorkflowDuration()

    inputDuration(workflowParameter, hms) {
      const duration = this.stringToDuration(hms);
      this.inputValue(workflowParameter, duration);
    }, // inputDuration()

    getMaintenanceSuffix(interval, value) {
      return this.$tc(`programs.workflows.workflowDetails.maintenanceIntervalSuffixes.S${interval}`, value);
    },

    validateProbeWorkflow(val) {
      if (!val) {
        this.showAdvancedSettings = true;
      }

      return !!val
        || this.$t('programs.workflows.workflowDetails.selectProbeToStatusValidator');
    }, // validateProbeWorkflow()
  },

};
</script>
